import type { ReactElement, ReactNode } from 'react';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { getUnixTime } from 'date-fns';
import { decode } from 'jwt-simple';
import Loading from 'atom-components/loading';
import { BROADCAST_MESSAGES, TokenUtil } from 'services/TokenUtil';
import { postChangeCustomer } from 'services/assist-api/actions/changeCustomer';
import { postLogin } from 'services/assist-api/actions/login';
import { postLogout } from 'services/assist-api/actions/logout';
import { masquerade } from 'services/assist-api/actions/masquerade';
import { masqueradeExit } from 'services/assist-api/actions/masqueradeExit';
import { refreshAccessToken } from 'services/assist-api/actions/refresh-token';
import type { Me } from 'services/assist-api/entities/me/me';
import { useMe } from 'services/assist-api/entities/me/me';
import { AssistQueryClient } from 'services/assist-api/query-client';

const INTERVAL_AUTO_REFRESH = 60; // check every 1min
const LEAD_SECONDS_BEFORE_LOGOUT = 20; // logout if will expire in next 20 seconds
const LEAD_SECONDS_BEFORE_REFRESH = 60 * 2; // refresh token if only valid for another 2min

type Auth = {
  isLoading: boolean;
  isAuthenticated: boolean;
  isMasquerading: boolean;
  me?: Me;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  startMasquerade(userId: number): Promise<void>;
  endMasquerade(): Promise<void>;
  changeCustomer(customerId: number): Promise<void>;
};

export const AuthContext = createContext<Auth>({
  isLoading: false,
  isAuthenticated: false,
  isMasquerading: false,
  me: undefined,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  startMasquerade: () => Promise.resolve(),
  endMasquerade: () => Promise.resolve(),
  changeCustomer: () => Promise.resolve()
});

/* check if we are storing any access tokens */
const isLocalTokens = (): boolean => !!TokenUtil.getAccessToken() && !!TokenUtil.getRefreshToken();

// Context via RenderProps
type ProviderPropsOptionalValue<T> = {
  value?: T; // Not used in main index.tsx
  children?: ReactNode | undefined;
};

/**
 * Manage users authentication state
 *
 * - only renders children if auth state can be determined
 * - provides login function
 * - provides logout function
 * - refreshes the users token
 */
export function AuthProvider(
  props: JSX.IntrinsicAttributes & ProviderPropsOptionalValue<Auth>
): ReactElement {
  const me = useMe({ enabled: isLocalTokens() }); // auth user info

  /* local loading state */
  const [isLoading, setLoading] = useState(!me.isFetched);
  useEffect(() => setLoading(me.isLoading && isLocalTokens()), [me.isLoading]);

  const isAuthenticated = isLocalTokens() && me.isSuccess && me.isFetched;
  const isMasquerading = !!TokenUtil.getBackupAccessToken();

  const login = useCallback(
    async (username: string, password: string) => {
      /* login and get token info */
      const { token, refresh_token } = await postLogin({ username, password });
      TokenUtil.setAccessToken(token);
      TokenUtil.setRefreshToken(refresh_token);
      /* if there are any old backup tokens remove them (this ensures masquerading state is reset if authentication expires between logins) */
      TokenUtil.removeBackupTokens();
      /* get auth user info */
      await me.refetch();
    },
    [me]
  );

  const logout = useCallback(async () => {
    setLoading(true);
    /* logout - invalidate tokens and remove from storage */
    try {
      // logouts even if API part fails
      await postLogout(localStorage.getItem('access_token'), localStorage.getItem('refresh_token'));
    } finally {
      TokenUtil.removeAccessToken();
      TokenUtil.removeRefreshToken();

      // If we don't clear the backup tokens, the user will be masquerading as
      // themselves when they log back in again:
      TokenUtil.removeBackupTokens();

      /* clear auth user info */
      me.remove();
      setLoading(false);
    }
  }, [me]);

  const startMasquerade = useCallback(
    async (userId: number) => {
      await masquerade(userId);

      // Remove the cache as data will have been fetched as a different user with different access
      // permissions.
      AssistQueryClient.removeQueries();

      /* Ensure that user is refetched to prevent any info about the real user
        leaking. */
      await me.refetch();
    },
    [me]
  );

  const endMasquerade = useCallback(async () => {
    await masqueradeExit();

    // Remove the cache as data will have been fetched as a different user with different access
    // permissions.
    AssistQueryClient.removeQueries();

    /* Ensure that the user is refetched to prevent any info about the
       masquerade user leaking. */
    await me.refetch();
  }, [me]);

  const changeCustomer = useCallback(
    async (customerId: number) => {
      try {
        setLoading(true);
        const { token, refresh_token } = await postChangeCustomer(customerId);
        TokenUtil.setAccessToken(token);
        TokenUtil.setRefreshToken(refresh_token);
      } finally {
        me.remove();
        await me.refetch();
      }
    },
    [me]
  );

  /**
   * Check if the token is close to expiry or expired
   * - refresh the token with the API
   * - or logout the user
   */
  const refreshToken = useCallback(async () => {
    const token = TokenUtil.getAccessToken();
    if (!token) return;

    const { exp } = decode(token, null, true);
    const now = getUnixTime(new Date());

    // If the token will be invalid within 30 seconds or already invalid, logout the user
    if (exp - LEAD_SECONDS_BEFORE_LOGOUT < now) {
      logout();
    }
    // If the token will be soon, refresh the token
    else if (exp - LEAD_SECONDS_BEFORE_REFRESH < now) {
      const newToken = await refreshAccessToken(TokenUtil.getRefreshToken());
      TokenUtil.setAccessToken(newToken);
    }
  }, [logout]);

  /* Check the tokens and try and refresh it on a given time interval */
  useEffect(() => {
    // setup up an interval to run our function
    const handle = setInterval(refreshToken, INTERVAL_AUTO_REFRESH * 1000);
    // Clear interval when component is unmounted or auto refresh functions changes
    return () => {
      clearInterval(handle);
    };
  }, [refreshToken]);

  /* setup broadcast listener (reacts to changes in other browser tabs) */
  useEffect(() => {
    TokenUtil.getBroadcastChannel().onmessage = (msg): void => {
      if (
        msg === BROADCAST_MESSAGES.ACCESS_TOKEN_SET ||
        msg === BROADCAST_MESSAGES.ACCESS_TOKEN_REMOVE
      ) {
        me.refetch();
      }
    };
  }, [me]);

  const value = useMemo(
    () => ({
      login,
      logout,
      startMasquerade,
      endMasquerade,
      changeCustomer,
      isLoading,
      isAuthenticated,
      isMasquerading,
      me: me.data
    }),
    [
      changeCustomer,
      isAuthenticated,
      isMasquerading,
      isLoading,
      login,
      logout,
      startMasquerade,
      endMasquerade,
      me.data
    ]
  );

  if (isLoading) {
    return <Loading />;
  }

  return <AuthContext.Provider value={value} {...props} />;
}

export const useAuth = (): Auth => React.useContext(AuthContext);
